import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { Ban, Bell, CheckCircle2, Lock, User as UserIcon, XCircle } from "lucide-react";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Spinner } from "components/ui/spinner";
import { useUser } from "utils/hooks/user";
import { useNotifications } from "features/Notifications/context";
import { useNotificationRedirect } from "features/Notifications/useNotificationRedirect";
import { cn } from "lib/utils";

type Notification = {
    id: string;
    read?: boolean;
    title?: string;
    body?: string;
    createdAt?: string;
    type: string;
    data?: { status?: string; [key: string]: unknown };
};

const STATUS_ICONS: Record<string, { icon: typeof UserIcon; className: string }> = {
    approved: { icon: CheckCircle2, className: "tw-text-emerald-600" },
    denied: { icon: XCircle, className: "tw-text-rose-500" },
    canceled: { icon: Ban, className: "tw-text-muted-foreground" },
    "pending-canceled": { icon: Ban, className: "tw-text-amber-500" },
    closed: { icon: Lock, className: "tw-text-muted-foreground" },
};

function NotificationItem({ notification, onClick }: { notification: Notification; onClick: (n: Notification) => void }) {
    const status = notification.data?.status;
    const { icon: Icon, className: iconClassName } = (status && STATUS_ICONS[status])
        || { icon: UserIcon, className: "tw-text-muted-foreground" };

    return (
        <DropdownMenuItem
            className={cn(
                "tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-whitespace-normal tw-py-2",
                !notification.read && "tw-bg-accent"
            )}
            onClick={() => onClick(notification)}
        >
            <Icon className={cn("tw-mt-0.5 tw-size-4 tw-shrink-0", iconClassName)} />

            <div className="tw-min-w-0 tw-flex-1">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                    <h4 className="tw-m-0 tw-truncate tw-text-sm tw-font-semibold">{notification?.title}</h4>
                    <span className="tw-shrink-0 tw-text-xs tw-text-muted-foreground">
                        {moment(notification?.createdAt).fromNow()}
                    </span>
                </div>
                <p className="tw-m-0 tw-truncate tw-text-sm tw-text-muted-foreground">{notification?.body}</p>
            </div>
        </DropdownMenuItem>
    );
}

function Notifications() {
    const { t } = useTranslation();
    const user = useUser();
    const navigate = useNavigate();

    const { notifications, markRead, markAllRead, refresh, loading, unreadNotifications } = useNotifications();

    const redirectNotification = useNotificationRedirect();
    const openNotification = useCallback(
        (notification: Notification) => {
            if (!notification.read) {
                markRead(notification.id);
            }
            redirectNotification(notification.type, notification.data);
        },
        [markRead, redirectNotification]
    );

    // open-shift-available can come back to the top as unread, so reload whenever the bell opens
    const onOpenChange = useCallback(
        (open: boolean) => {
            if (open) {
                refresh();
            }
        },
        [refresh]
    );

    return (
        <DropdownMenu onOpenChange={onOpenChange}>
            <DropdownMenuTrigger asChild>
                <button className="tw-relative tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-md tw-text-inherit tw-opacity-75 tw-outline-none hover:tw-opacity-100">
                    <Bell className="tw-size-5" />
                    {unreadNotifications > 0 && (
                        <span className="tw-absolute tw-top-0 tw-right-0 tw-flex tw-h-4 tw-min-w-4 tw-items-center tw-justify-center tw-rounded-full tw-bg-destructive tw-px-1 tw-text-[10px] tw-font-semibold tw-text-destructive-foreground">
                            {unreadNotifications}
                        </span>
                    )}
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="end"
                className="tw-w-[420px] tw-max-w-[90vw] tw-p-0"
                // returning focus to the bell would dismiss drawers a notification just opened
                onCloseAutoFocus={(e) => e.preventDefault()}
            >
                <div className="tw-px-3 tw-py-3">
                    <h6 className="tw-m-0 tw-text-sm tw-text-muted-foreground">
                        {t("count-notifications", { count: unreadNotifications })}
                    </h6>
                </div>

                <div className="tw-max-h-[250px] tw-overflow-y-auto tw-px-1">
                    {notifications.map((notification: Notification) => (
                        <NotificationItem key={notification.id} notification={notification} onClick={openNotification} />
                    ))}

                    {loading && (
                        <div className="tw-flex tw-justify-center tw-py-3">
                            <Spinner />
                        </div>
                    )}
                </div>

                <div className="tw-flex tw-items-center tw-justify-center tw-gap-5 tw-px-3 tw-py-3">
                    {!loading && unreadNotifications > 0 && (
                        <h4
                            className="tw-m-0 tw-cursor-pointer tw-text-sm tw-font-bold tw-text-primary"
                            onClick={markAllRead}
                        >
                            {t("read-all")}
                        </h4>
                    )}
                    <div
                        className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center"
                        onClick={() => navigate("/notifications")}
                    >
                        <h4 className="tw-m-0 tw-text-sm tw-text-primary">{t("view-all")}</h4>
                    </div>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default Notifications;
